import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "../i18n/LangContext";
import { api, extractError } from "../api/client";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { AUTH } from "../constants/testIds";
import { ERROR_CODE_MAP } from "../i18n/translations";

const STEPS = ["stepAccount", "stepProfile", "stepProgram", "stepReview"];
const NONE = "__none__";

export default function Register() {
  const { t, lang } = useLang();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    full_name: "", email: "", password: "", confirm_password: "",
    whatsapp_number: "", program_id: NONE, coach_preference_id: NONE,
  });
  const [options, setOptions] = useState({ programs: [], coaches: [] });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [busy, setBusy] = useState(false);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    api.get("/auth/registration-options")
      .then(({ data }) => setOptions(data.data))
      .catch(() => {});
  }, []);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const pick = (k) => (v) => setForm({ ...form, [k]: v });
  const programName = options.programs.find((p) => p.program_id === form.program_id)?.name || t("auth.regProgramNone");
  const coachName = options.coaches.find((c) => c.user_id === form.coach_preference_id)?.full_name || t("auth.regCoachAny");

  const next = () => {
    setError("");
    if (step === 0) {
      if (!form.full_name || !form.email || !form.password) return setError(t("validation.required"));
      if (form.password.length < 8) return setError(t("validation.passwordWeak"));
      if (form.password !== form.confirm_password) return setError(t("validation.passwordMismatch"));
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.post("/auth/register", {
        full_name: form.full_name,
        email: form.email,
        password: form.password,
        confirm_password: form.confirm_password,
        whatsapp_number: form.whatsapp_number || null,
        program_id: form.program_id === NONE ? null : form.program_id,
        coach_preference_id: form.coach_preference_id === NONE ? null : form.coach_preference_id,
        accept_terms: accepted,
        preferred_language: lang,
      });
      setSuccess(t("auth.registerSuccess"));
    } catch (err) {
      const { code, message } = extractError(err);
      const key = ERROR_CODE_MAP[code];
      setError(key ? t(`auth.${key}`) : message);
    } finally {
      setBusy(false);
    }
  };

  if (success) {
    return (
      <div className="space-y-5">
        <h1 className="font-[Outfit] text-3xl font-extrabold text-white tracking-tight">{t("auth.registerTitle")}</h1>
        <div data-testid={AUTH.registerSuccess} className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-300">
          {success}
        </div>
        <Link data-testid={AUTH.toLogin} to="/login" className="inline-block text-white font-semibold hover:text-[#FF3B30]">→ {t("auth.loginLink")}</Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h1 className="font-[Outfit] text-3xl font-extrabold text-white tracking-tight">{t("auth.registerTitle")}</h1>
        <p className="text-slate-400 text-sm">{t("auth.registerSubtitle")}</p>
      </div>

      <div className="flex items-center gap-2" data-testid="register-stepper">
        {STEPS.map((s, i) => (
          <div key={s} className="flex-1">
            <div className={`h-1 rounded-full transition-colors ${i <= step ? "bg-[#FF3B30]" : "bg-white/10"}`} />
            <div className={`mt-2 text-[10px] uppercase tracking-widest ${i === step ? "text-white" : "text-slate-500"}`}>{t(`auth.${s}`)}</div>
          </div>
        ))}
      </div>

      <form onSubmit={submit} className="space-y-4">
        {step === 0 && (
          <>
            <div className="space-y-2">
              <Label className="text-slate-300">{t("common.fullName")}</Label>
              <Input data-testid={AUTH.registerName} value={form.full_name} onChange={upd("full_name")} className="bg-[#1C202E] border-white/10 text-white h-11" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">{t("common.email")}</Label>
              <Input data-testid={AUTH.registerEmail} type="email" value={form.email} onChange={upd("email")} className="bg-[#1C202E] border-white/10 text-white h-11" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">{t("common.password")}</Label>
                <Input data-testid={AUTH.registerPassword} type="password" value={form.password} onChange={upd("password")} className="bg-[#1C202E] border-white/10 text-white h-11" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">{t("common.confirmPassword")}</Label>
                <Input data-testid={AUTH.registerConfirm} type="password" value={form.confirm_password} onChange={upd("confirm_password")} className="bg-[#1C202E] border-white/10 text-white h-11" />
              </div>
            </div>
          </>
        )}

        {step === 1 && (
          <div className="space-y-2">
            <Label className="text-slate-300">{t("common.whatsapp")}</Label>
            <Input data-testid={AUTH.registerWhatsapp} value={form.whatsapp_number} onChange={upd("whatsapp_number")} className="bg-[#1C202E] border-white/10 text-white h-11" placeholder="+62..." />
            <p className="text-xs text-slate-500">{t("auth.regWhatsappHint")}</p>
          </div>
        )}

        {step === 2 && (
          <>
            <div className="space-y-2">
              <Label className="text-slate-300">{t("auth.regProgram")}</Label>
              <Select value={form.program_id} onValueChange={pick("program_id")}>
                <SelectTrigger data-testid="register-program-select" className="bg-[#1C202E] border-white/10 text-white h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#161923] border-white/10 text-slate-200">
                  <SelectItem value={NONE}>{t("auth.regProgramNone")}</SelectItem>
                  {options.programs.map((p) => (
                    <SelectItem key={p.program_id} value={p.program_id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">{t("auth.regCoach")}</Label>
              <Select value={form.coach_preference_id} onValueChange={pick("coach_preference_id")}>
                <SelectTrigger data-testid="register-coach-select" className="bg-[#1C202E] border-white/10 text-white h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#161923] border-white/10 text-slate-200">
                  <SelectItem value={NONE}>{t("auth.regCoachAny")}</SelectItem>
                  {options.coaches.map((c) => (
                    <SelectItem key={c.user_id} value={c.user_id}>{c.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">{t("auth.regCoachHint")}</p>
            </div>
          </>
        )}

        {step === 3 && (
          <div data-testid="register-review" className="rounded-xl border border-white/5 bg-white/[0.02] divide-y divide-white/5">
            {[
              [t("common.fullName"), form.full_name],
              [t("common.email"), form.email],
              [t("common.whatsapp"), form.whatsapp_number || "—"],
              [t("auth.regProgram"), programName],
              [t("auth.regCoach"), coachName],
              [t("dashboard.role"), t("roles.MEMBER")],
            ].map(([k, v]) => (
              <div key={k} className="flex items-center justify-between gap-4 px-4 py-3 text-sm">
                <span className="text-slate-500">{k}</span>
                <span className="text-white font-medium text-right break-all">{v}</span>
              </div>
            ))}
          </div>
        )}

        {step === 3 && (
          <label className="flex items-start gap-3 text-sm text-slate-300">
            <input type="checkbox" data-testid="register-accept-terms" checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)} className="mt-1 accent-[#FF3B30]" />
            <span>
              {t("legal.agree")}{" "}
              <Link to="/terms" target="_blank" className="text-white underline hover:text-[#FF3B30]">{t("legal.terms")}</Link>{" "}
              {t("legal.agreeAnd")}{" "}
              <Link to="/privacy" target="_blank" className="text-white underline hover:text-[#FF3B30]">{t("legal.privacy")}</Link>.
            </span>
          </label>
        )}

        {error && <div data-testid={AUTH.registerError} className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">{error}</div>}

        <div className="flex gap-3">
          {step > 0 && (
            <Button data-testid="register-back-btn" type="button" variant="outline" onClick={() => setStep(step - 1)} className="h-11 border-white/15 bg-transparent text-slate-200 hover:bg-white/5 hover:text-white">
              {t("common.back")}
            </Button>
          )}
          {step < STEPS.length - 1 ? (
            <Button data-testid="register-next-btn" type="button" onClick={next} className="flex-1 h-11 bg-[#FF3B30] hover:bg-[#E02E24] text-white font-semibold">
              {t("auth.regNext")}
            </Button>
          ) : (
            <Button data-testid={AUTH.registerSubmit} type="submit" disabled={busy || !accepted} className="flex-1 h-11 bg-[#FF3B30] hover:bg-[#E02E24] text-white font-semibold">
              {busy ? t("common.loading") : t("auth.registerSubmit")}
            </Button>
          )}
        </div>
      </form>

      <div className="text-center text-sm text-slate-400">
        {t("auth.alreadyAccount")}{" "}
        <Link data-testid={AUTH.toLogin} to="/login" className="text-white font-semibold hover:text-[#FF3B30]">{t("auth.loginLink")}</Link>
      </div>
    </div>
  );
}
